import type { BackendDashboardSpec, DashboardDataResponseSpec } from "../Processing/types";
import { trainingAPI } from "../../../api/TrainingAPI";

/** One SSE frame. `type` is the only field every route guarantees. */
export interface AiStreamFrame {
  type: string;
  [key: string]: unknown;
}

/** How the stream finished. "dropped" = no terminal frame arrived. */
export type AiStreamOutcome = "done" | "error" | "dropped";

/**
 * A gate/payload failure, always sent as JSON before the first byte. `status`
 * is what the tile maps to a state (400 empty query → configure-me, 403 →
 * no-access, 429 → throttled, everything else → error).
 */
export class AiStreamRequestError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Seconds to wait, from Retry-After or the DRF throttle detail. 429 only. */
    readonly retryAfter?: number,
  ) {
    super(message);
    this.name = "AiStreamRequestError";
  }
}

/** DRF's throttle detail is prose: "Request was throttled. Expected available in 42 seconds." */
function parseRetryAfter(response: Response, body: Record<string, unknown>): number {
  const header = Number(response.headers.get("Retry-After"));
  if (Number.isFinite(header) && header > 0) return header;
  const seconds = /(\d+)\s*second/.exec(String(body.detail ?? ""));
  return seconds ? Number(seconds[1]) : 60;
}

/**
 * Post the dashboard to the AI Insights endpoint and pump its SSE frames into
 * `onFrame`. Resolves once the stream ends; rejects with AiStreamRequestError
 * on a pre-first-byte failure, or the abort reason when `signal` fires.
 *
 * Endpoint: POST /api/dashboard-insights → 200 text/event-stream, or JSON error.
 */
export async function streamDashboardInsights(
  args: {
    dashboardId: string;
    dashboardSpec: BackendDashboardSpec;
    dashboardData: DashboardDataResponseSpec;
    /** Bare (unprefixed) widget id. Required by the backend. */
    widgetId: string;
    force: boolean;
  },
  onFrame: (frame: AiStreamFrame) => void,
  signal: AbortSignal,
): Promise<AiStreamOutcome> {
  const response = await trainingAPI.postDashboardInsightsStream({ ...args, signal });

  if (!response.ok) {
    // Read the body exactly once — a 403 can be a bare string, not JSON.
    const raw = await response.text().catch(() => "");
    let body: Record<string, unknown> = {};
    try {
      body = raw ? JSON.parse(raw) : {};
    } catch {
      body = { error: raw };
    }
    const message = String(body.error ?? body.message ?? body.detail ?? raw ?? `HTTP ${response.status}`);
    throw new AiStreamRequestError(
      response.status,
      message,
      response.status === 429 ? parseRetryAfter(response, body) : undefined,
    );
  }
  if (!response.body) throw new AiStreamRequestError(response.status, "No response body");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (!jsonStr) continue;
      let frame: AiStreamFrame;
      try {
        frame = JSON.parse(jsonStr);
      } catch {
        continue;
      }
      onFrame(frame);
      // Terminal frames end the turn; anything after them is not ours to read.
      if (frame.type === "turn_done") return "done";
      if (frame.type === "error") return "error";
    }
  }

  return "dropped";
}
